import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROUTES, type Task, type TaskInput } from '@serb/shared';
import { api, errorText } from '../../api/client';
import { KEYS } from '../../api/keys';
import { SAMPLE_DATA } from '../../lib/sample';
import { CLAUDE_TASKS } from './sample';

/**
 * The jobs given to Claude. They are kept on the server; the server wakes Claude for each one and it reports back here.
 * With sample data on, made-up tasks are kept in the browser instead.
 */
export function useClaudeTasks() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: KEYS.tasks,
    queryFn: SAMPLE_DATA ? async () => CLAUDE_TASKS : () => api.get<Task[]>(ROUTES.api.tasks),
    staleTime: SAMPLE_DATA ? Infinity : 0,
    gcTime: SAMPLE_DATA ? Infinity : undefined,
  });

  const add = async (input: TaskInput) => {
    if (SAMPLE_DATA) {
      const at = new Date().toISOString();
      const task: Task = {
        id: `task-${Date.now()}`,
        task: input.task,
        code: input.code ?? '',
        runAt: null,
        rule: input.rule ?? false,
        status: 'waiting',
        result: '',
        cardId: null,
        createdAt: at,
        updatedAt: at,
      };
      qc.setQueryData<Task[]>(KEYS.tasks, (all = []) => [task, ...all]);
      return task;
    }
    const task = await api.post<Task>(ROUTES.api.tasks, input);
    await qc.invalidateQueries({ queryKey: KEYS.tasks });
    return task;
  };

  return {
    items: q.data ?? [],
    loading: q.isPending,
    error: q.isError ? errorText(q.error, 'The tasks could not be loaded.') : '',
    add,
  };
}
